package com.daytimegaming.ourdailyfinances.presentation.components

import androidx.compose.animation.animateColorAsState
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.Saver
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.daytimegaming.ourdailyfinances.domain.model.DashboardAccount
import com.daytimegaming.ourdailyfinances.domain.model.Holding
import com.daytimegaming.ourdailyfinances.domain.model.RealEstateHolding
import com.daytimegaming.ourdailyfinances.domain.model.SecurityPerformance
import com.daytimegaming.ourdailyfinances.domain.model.Transaction
import com.daytimegaming.ourdailyfinances.presentation.theme.GlassCard
import com.daytimegaming.ourdailyfinances.presentation.util.formatAmount
import com.daytimegaming.ourdailyfinances.presentation.util.formatWholePart
import com.daytimegaming.ourdailyfinances.presentation.util.monthDayYearFormat
import kotlin.math.abs
import kotlin.math.round
import kotlin.time.Clock
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlinx.datetime.todayIn

@Composable
fun BalanceOverTimeCard(
    points: List<Pair<LocalDate, Double>>,
    selectedTimeframe: BalanceTimeframe,
    onTimeframeSelected: (BalanceTimeframe) -> Unit,
    modifier: Modifier = Modifier,
    totalBalance: Double? = null,
    currencySymbol: String = "$",
    isLoading: Boolean = false,
    loadingMessage: String = "Loading performance data...",
    showPeriodChange: Boolean = true,
    showDateLabels: Boolean = true,
    chartHeight: Dp = 180.dp,
    footnote: String? = "** Historical balance reconstructed from transactions",
    warningMessage: String? = null,
) {
    val startPoint = points.firstOrNull()
    val endPoint = points.lastOrNull()

    val fallbackBalance = totalBalance ?: 0.0
    val startBalance = startPoint?.second ?: fallbackBalance
    val endBalance = endPoint?.second ?: fallbackBalance
    val balanceChange = endBalance - startBalance
    val percentChange = if (startBalance != 0.0) (balanceChange / startBalance) * 100.0 else 0.0
    val absPercent = abs(percentChange)

    val primaryColor = MaterialTheme.colorScheme.primary
    val gridColor = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.05f)

    GlassCard(modifier = modifier) {
        Column(modifier = Modifier.fillMaxWidth()) {
            // Segmented Control
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically
            ) {
                BalanceHistorySegmentedControl(
                    selectedTimeframe = selectedTimeframe,
                    onTimeframeSelected = onTimeframeSelected
                )
            }

            if (showPeriodChange) {
                Spacer(modifier = Modifier.height(16.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.Bottom
                ) {
                    val changeText = if (balanceChange >= 0) {
                        "+$currencySymbol${balanceChange.formatAmount()} (+${absPercent.formatAmount()}%)"
                    } else {
                        "-$currencySymbol${abs(balanceChange).formatAmount()} (-${absPercent.formatAmount()}%)"
                    }
                    val changeColor = if (balanceChange >= 0) {
                        MaterialTheme.colorScheme.primary
                    } else {
                        MaterialTheme.colorScheme.error
                    }

                    Text(
                        text = "Period Change",
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Spacer(modifier = Modifier.height(2.dp))
                    Text(
                        text = changeText,
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = FontWeight.Bold,
                        color = changeColor
                    )
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            if (isLoading) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(chartHeight),
                    contentAlignment = Alignment.Center
                ) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Center
                    ) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(36.dp),
                            color = primaryColor
                        )
                        Spacer(modifier = Modifier.height(8.dp))
                        Text(
                            text = loadingMessage,
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                }
            } else {
                BalanceHistoryChart(
                    points = points,
                    currencySymbol = currencySymbol,
                    primaryColor = primaryColor,
                    gridColor = gridColor,
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(chartHeight)
                )
            }

            if (showDateLabels && !isLoading && startPoint != null && endPoint != null) {
                Spacer(modifier = Modifier.height(12.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(
                        text = startPoint.first.monthDayYearFormat(),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Text(
                        text = endPoint.first.monthDayYearFormat(),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }

            if (!footnote.isNullOrBlank()) {
                Spacer(modifier = Modifier.height(16.dp))
                Text(
                    modifier = Modifier.fillMaxWidth(),
                    text = footnote,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    textAlign = TextAlign.End,
                )
            }

            if (!warningMessage.isNullOrBlank()) {
                Spacer(modifier = Modifier.height(if (!footnote.isNullOrBlank()) 4.dp else 16.dp))
                Text(
                    modifier = Modifier.fillMaxWidth(),
                    text = warningMessage,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    textAlign = TextAlign.End,
                )
            }
        }
    }
}

@Composable
fun BalanceHistoryChart(
    points: List<Pair<LocalDate, Double>>,
    currencySymbol: String,
    primaryColor: Color,
    gridColor: Color,
    modifier: Modifier = Modifier
) {
    if (points.size < 2) {
        Box(
            modifier = modifier,
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = "Insufficient data to display balance history",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
        return
    }

    val textMeasurer = rememberTextMeasurer()
    val labelColor = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.7f)
    val labelStyle = MaterialTheme.typography.labelSmall.copy(
        color = labelColor,
        fontSize = 10.sp
    )

    val firstDate = points.first().first
    val lastDate = points.last().first
    val totalDays = (lastDate.toEpochDays() - firstDate.toEpochDays()).toFloat().coerceAtLeast(1f)

    val minBalance = points.minOfOrNull { it.second } ?: 0.0
    val maxBalance = points.maxOfOrNull { it.second } ?: 0.0
    val range = maxBalance - minBalance
    val highestValue = abs(maxBalance).coerceAtLeast(abs(minBalance))
    val padding = if (range > 0.0) range * 0.1 else if (highestValue > 0.0) highestValue * 0.1 else 10.0
    val minVal = minBalance - padding
    val maxVal = maxBalance + padding
    val valRange = (maxVal - minVal).coerceAtLeast(0.01)

    val gridLines = 4
    val gridLabels = remember(minVal, valRange, currencySymbol) {
        (0..gridLines).map { i ->
            val ratio = i.toFloat() / gridLines
            val gridVal = minVal + ratio * valRange
            formatShortenedAmount(gridVal, currencySymbol)
        }
    }

    Canvas(modifier = modifier) {
        val width = size.width
        val height = size.height

        val maxLabelWidth = gridLabels.maxOfOrNull { textMeasurer.measure(it, labelStyle).size.width } ?: 0
        val paddingLeft = (maxLabelWidth + 12.dp.toPx()).coerceAtLeast(55.dp.toPx())
        val paddingRight = 0f
        val paddingTop = 10.dp.toPx()
        val paddingBottom = 10.dp.toPx()

        val graphWidth = width - paddingLeft - paddingRight
        val graphHeight = height - paddingTop - paddingBottom

        // Draw grid lines and labels
        for (i in 0..gridLines) {
            val ratio = i.toFloat() / gridLines
            val y = paddingTop + graphHeight * (1f - ratio)

            drawLine(
                color = gridColor,
                start = Offset(paddingLeft, y),
                end = Offset(width, y),
                strokeWidth = 1.dp.toPx()
            )

            val formattedVal = gridLabels[i]

            val layoutResult = textMeasurer.measure(formattedVal, labelStyle)
            val textWidth = layoutResult.size.width
            val textHeight = layoutResult.size.height

            drawText(
                textMeasurer = textMeasurer,
                text = formattedVal,
                topLeft = Offset(
                    x = paddingLeft - textWidth - 8.dp.toPx(),
                    y = y - textHeight / 2f
                ),
                style = labelStyle
            )
        }

        // Draw points
        val currentPath = Path()
        val fillPath = Path()

        points.forEachIndexed { index, (date, balance) ->
            val dayOffset = (date.toEpochDays() - firstDate.toEpochDays()).toFloat()
            val x = paddingLeft + (dayOffset / totalDays) * graphWidth
            val y = paddingTop + graphHeight * (1f - ((balance - minVal) / valRange).toFloat())

            if (index == 0) {
                currentPath.moveTo(x, y)
                fillPath.moveTo(x, paddingTop + graphHeight)
                fillPath.lineTo(x, y)
            } else {
                currentPath.lineTo(x, y)
                fillPath.lineTo(x, y)
            }

            if (index == points.lastIndex) {
                fillPath.lineTo(x, paddingTop + graphHeight)
            }
        }

        // Draw filled area underneath the line
        drawPath(
            path = fillPath,
            brush = Brush.verticalGradient(
                colors = listOf(
                    primaryColor.copy(alpha = 0.15f),
                    Color.Transparent
                )
            )
        )

        // Draw line path
        drawPath(
            path = currentPath,
            color = primaryColor,
            style = Stroke(
                width = 3.dp.toPx(),
                cap = StrokeCap.Round
            )
        )

        // Draw point and highlight circle at the end
        val lastPoint = points.last()
        val lastDayOffset = (lastPoint.first.toEpochDays() - firstDate.toEpochDays()).toFloat()
        val lastX = paddingLeft + (lastDayOffset / totalDays) * graphWidth
        val lastY = paddingTop + graphHeight * (1f - ((lastPoint.second - minVal) / valRange).toFloat())

        drawCircle(
            color = primaryColor.copy(alpha = 0.2f),
            radius = 8.dp.toPx(),
            center = Offset(lastX, lastY)
        )
        drawCircle(
            color = primaryColor,
            radius = 4.dp.toPx(),
            center = Offset(lastX, lastY)
        )
    }
}

@Composable
fun BalanceHistorySegmentedControl(
    selectedTimeframe: BalanceTimeframe,
    onTimeframeSelected: (BalanceTimeframe) -> Unit,
    modifier: Modifier = Modifier
) {
    val options = BalanceTimeframe.entries.toTypedArray()
    Row(
        modifier = modifier
            .clip(RoundedCornerShape(8.dp))
            .background(Color(0x15FFFFFF))
            .padding(2.dp),
        horizontalArrangement = Arrangement.spacedBy(4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        options.forEach { option ->
            val isSelected = option == selectedTimeframe
            val backgroundColor by animateColorAsState(
                targetValue = if (isSelected) MaterialTheme.colorScheme.primary else Color.Transparent,
                label = "bgColor"
            )
            val contentColor by animateColorAsState(
                targetValue = if (isSelected) Color(0xFF003731) else MaterialTheme.colorScheme.onSurfaceVariant,
                label = "contentColor"
            )

            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(6.dp))
                    .background(backgroundColor)
                    .clickable { onTimeframeSelected(option) }
                    .padding(horizontal = 12.dp, vertical = 6.dp),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = option.displayName,
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.Bold,
                    color = contentColor
                )
            }
        }
    }
}

@Composable
fun DashboardBalanceOverTimeCard(
    accounts: List<DashboardAccount>,
    transactions: List<Transaction>,
    modifier: Modifier = Modifier,
    holdings: List<Holding> = emptyList(),
    securityPerformance: SecurityPerformance? = null,
    realEstateHoldings: List<RealEstateHolding> = emptyList(),
    selectedTimeframe: BalanceTimeframe? = null,
    onTimeframeSelected: ((BalanceTimeframe) -> Unit)? = null,
    isSecurityPerformanceLoading: Boolean = false,
    currencySymbol: String = "$",
    showReconstructedFootnote: Boolean = true,
) {
    var internalTimeframe by rememberSaveable(
        stateSaver = Saver(
            save = { it.name },
            restore = { name -> BalanceTimeframe.entries.find { it.name == name } ?: BalanceTimeframe.SixMonths }
        )
    ) {
        mutableStateOf(BalanceTimeframe.SixMonths)
    }
    val activeTimeframe = selectedTimeframe ?: internalTimeframe
    val handleTimeframeSelected: (BalanceTimeframe) -> Unit = { timeframe ->
        internalTimeframe = timeframe
        onTimeframeSelected?.invoke(timeframe)
    }

    val chartPoints = remember(accounts, transactions, holdings, securityPerformance, realEstateHoldings, activeTimeframe) {
        computeDashboardBalanceHistory(accounts, transactions, holdings, securityPerformance, realEstateHoldings, activeTimeframe)
    }

    val totalCurrentBalance = accounts.sumOf { account ->
        val rawBal = account.currentBalance ?: 0.0
        val isInvestment = account.type.trim().lowercase() == "investment"
        val bal = if (isInvestment && rawBal <= 0.0) {
            val acctHoldings = holdings.filter { it.accountId == account.accountId }
            val holdingsVal = acctHoldings.sumOf { it.institutionValue ?: (it.quantity * it.institutionPrice) }
            if (holdingsVal > 0.0) holdingsVal else rawBal
        } else {
            rawBal
        }
        if (account.type.trim().lowercase() == "credit" || account.type.trim().lowercase() == "loan") -bal else bal
    } + realEstateHoldings.sumOf { it.netValue }

    val anyInvestments = accounts.any { account -> account.type.lowercase() == "investment" }
    val anyProxies = holdings.any { it.proxySecurity != null || !it.security?.proxySecurityId.isNullOrBlank() }
    val warningMessage = if (anyInvestments && anyProxies) {
        "One or more of the holdings in this account uses a proxy security holding. Historical performance data for these securities may not always be accurate."
    } else null

    BalanceOverTimeCard(
        points = chartPoints,
        selectedTimeframe = activeTimeframe,
        onTimeframeSelected = handleTimeframeSelected,
        totalBalance = totalCurrentBalance,
        currencySymbol = currencySymbol,
        isLoading = isSecurityPerformanceLoading,
        footnote = if (showReconstructedFootnote) "** Historical balance reconstructed from transactions" else null,
        warningMessage = warningMessage,
        modifier = modifier
    )
}

@Composable
fun AccountBalanceOverTimeCard(
    currentBalance: Double,
    transactions: List<Transaction>,
    modifier: Modifier = Modifier,
    holdings: List<Holding> = emptyList(),
    securityPerformance: SecurityPerformance? = null,
    isInvestment: Boolean = false,
    isSecurityPerformanceLoading: Boolean = false,
    currencySymbol: String = "$",
    selectedTimeframe: BalanceTimeframe? = null,
    onTimeframeSelected: ((BalanceTimeframe) -> Unit)? = null,
    showReconstructedFootnote: Boolean = true,
) {
    var internalTimeframe by rememberSaveable(
        stateSaver = Saver(
            save = { it.name },
            restore = { name -> BalanceTimeframe.entries.find { it.name == name } ?: BalanceTimeframe.SixMonths }
        )
    ) {
        mutableStateOf(BalanceTimeframe.SixMonths)
    }
    val activeTimeframe = selectedTimeframe ?: internalTimeframe
    val handleTimeframeSelected: (BalanceTimeframe) -> Unit = { timeframe ->
        internalTimeframe = timeframe
        onTimeframeSelected?.invoke(timeframe)
    }

    val chartPoints = remember(currentBalance, transactions, holdings, securityPerformance, activeTimeframe) {
        if (isInvestment) {
            computeInvestmentAccountBalanceHistory(currentBalance, holdings, securityPerformance, activeTimeframe)
        } else {
            computeBalanceHistory(currentBalance, transactions, activeTimeframe)
        }
    }

    val anyProxies = holdings.any { it.proxySecurity != null || !it.security?.proxySecurityId.isNullOrBlank() }
    val warningMessage = if (isInvestment && anyProxies) {
        "One or more of the holdings in this account uses a proxy security holding. Historical performance data for these securities may not always be accurate."
    } else null

    BalanceOverTimeCard(
        points = chartPoints,
        selectedTimeframe = activeTimeframe,
        onTimeframeSelected = handleTimeframeSelected,
        totalBalance = currentBalance,
        currencySymbol = currencySymbol,
        isLoading = isSecurityPerformanceLoading,
        footnote = if (showReconstructedFootnote) "** Historical balance reconstructed from transactions" else null,
        warningMessage = warningMessage,
        modifier = modifier
    )
}

enum class BalanceTimeframe(val displayName: String) {
    OneMonth("1M"),
    ThreeMonths("3M"),
    SixMonths("6M"),
    OneYear("YR"),
    YearToDate("YTD")
}

fun formatShortenedAmount(amount: Double, currencySymbol: String): String {
    val isNegative = amount < 0
    val absAmount = abs(amount)
    val sign = if (isNegative) "-" else ""
    val formatted = when {
        absAmount >= 1_000_000_000 -> {
            val value = absAmount / 1_000_000_000
            val formattedVal = formatUpToDecimals(value, maxDecimals = 3)
            "$sign$currencySymbol${formattedVal}b"
        }
        absAmount >= 1_000_000 -> {
            val value = absAmount / 1_000_000
            val formattedVal = formatUpToDecimals(value, maxDecimals = 3)
            "$sign$currencySymbol${formattedVal}m"
        }
        absAmount >= 1_000 -> {
            val value = round(absAmount / 1_000).toLong()
            "$sign$currencySymbol${formatWholePart(value)}k"
        }
        else -> {
            val formattedVal = formatWholePart(round(absAmount).toLong())
            "$sign$currencySymbol$formattedVal"
        }
    }
    return formatted
}

fun formatUpToDecimals(value: Double, maxDecimals: Int): String {
    var factor = 1.0
    repeat(maxDecimals) { factor *= 10.0 }
    val factorLong = factor.toLong()
    val roundedScaled = round(value * factor).toLong()
    val whole = roundedScaled / factorLong
    val remainder = roundedScaled % factorLong
    if (remainder == 0L) {
        return whole.toString()
    }
    val decStr = remainder.toString().padStart(maxDecimals, '0').trimEnd('0')
    return "$whole.$decStr"
}

fun computeBalanceHistory(
    currentBalance: Double,
    transactions: List<Transaction>,
    timeframe: BalanceTimeframe
): List<Pair<LocalDate, Double>> {
    val today = try {
        Clock.System.todayIn(TimeZone.currentSystemDefault())
    } catch (e: Exception) {
        Clock.System.todayIn(TimeZone.currentSystemDefault())
    }

    val startDate = when (timeframe) {
        BalanceTimeframe.OneMonth -> today.minus(1, DateTimeUnit.MONTH)
        BalanceTimeframe.ThreeMonths -> today.minus(3, DateTimeUnit.MONTH)
        BalanceTimeframe.SixMonths -> today.minus(6, DateTimeUnit.MONTH)
        BalanceTimeframe.OneYear -> today.minus(1, DateTimeUnit.YEAR)
        BalanceTimeframe.YearToDate -> LocalDate(today.year, 1, 1)
    }

    // Group transactions by date
    val groupedTx = transactions
        .mapNotNull { tx ->
            try {
                LocalDate.parse(tx.date.substringBefore('T')) to tx
            } catch (e: Exception) {
                null
            }
        }
        .groupBy({ pair -> pair.first }, { pair -> pair.second })

    // Sort dates descending
    val sortedDates = groupedTx.keys.sortedDescending()

    val dateToBalance = mutableMapOf<LocalDate, Double>()
    dateToBalance[today] = currentBalance

    var runningBalance = currentBalance
    for (date in sortedDates) {
        // The balance after this date's transactions is runningBalance
        dateToBalance[date] = runningBalance

        val dayTotalAmount = groupedTx[date]?.sumOf { tx -> tx.amount } ?: 0.0
        runningBalance += dayTotalAmount

        // The balance before this date's transactions (i.e. at the end of the previous day)
        dateToBalance[date.minus(1, DateTimeUnit.DAY)] = runningBalance
    }

    // Sort all recorded points ascending by date
    val history = dateToBalance.entries.sortedBy { it.key }

    // Helper to get balance on any date
    fun getBalanceOn(date: LocalDate): Double {
        if (history.isEmpty()) return currentBalance
        val match = history.lastOrNull { it.key <= date }
        return match?.value ?: history.first().value
    }

    val chartPoints = mutableListOf<Pair<LocalDate, Double>>()
    var currentDate = startDate
    while (currentDate <= today) {
        chartPoints.add(currentDate to getBalanceOn(currentDate))
        currentDate = currentDate.plus(1, DateTimeUnit.DAY)
    }

    return chartPoints
}

fun computeInvestmentAccountBalanceHistory(
    currentBalance: Double,
    holdings: List<Holding>,
    securityPerformance: SecurityPerformance?,
    timeframe: BalanceTimeframe
): List<Pair<LocalDate, Double>> {
    val today = try {
        Clock.System.todayIn(TimeZone.currentSystemDefault())
    } catch (e: Exception) {
        Clock.System.todayIn(TimeZone.currentSystemDefault())
    }

    val startDate = when (timeframe) {
        BalanceTimeframe.OneMonth -> today.minus(1, DateTimeUnit.MONTH)
        BalanceTimeframe.ThreeMonths -> today.minus(3, DateTimeUnit.MONTH)
        BalanceTimeframe.SixMonths -> today.minus(6, DateTimeUnit.MONTH)
        BalanceTimeframe.OneYear -> today.minus(1, DateTimeUnit.YEAR)
        BalanceTimeframe.YearToDate -> LocalDate(today.year, 1, 1)
    }

    val totalHoldingsValue = holdings.sumOf { it.institutionValue ?: (it.quantity * it.institutionPrice) }
    val effectiveBalance = if (currentBalance >= totalHoldingsValue && currentBalance > 0.0) currentBalance else totalHoldingsValue
    val uninvestedCash = (effectiveBalance - totalHoldingsValue).coerceAtLeast(0.0)
    val nonTickerHoldingsValue = holdings.filter { (it.proxySecurity?.tickerSymbol ?: it.security?.tickerSymbol)?.trim().isNullOrEmpty() }
        .sumOf { it.institutionValue ?: (it.quantity * it.institutionPrice) }
    val staticBaseValue = uninvestedCash + nonTickerHoldingsValue

    val activeTickers = holdings.mapNotNull {
        (it.proxySecurity?.tickerSymbol ?: it.security?.tickerSymbol)?.trim()?.takeIf { sym -> sym.isNotEmpty() }
    }.toSet()

    // If there are no active tickers at all, return flat line at current balance / holdings value
    if (activeTickers.isEmpty()) {
        val totalVal = if (currentBalance > 0.0) currentBalance else totalHoldingsValue
        val points = mutableListOf<Pair<LocalDate, Double>>()
        var currentDate = startDate
        while (currentDate <= today) {
            points.add(currentDate to totalVal)
            currentDate = currentDate.plus(1, DateTimeUnit.DAY)
        }
        return points
    }

    val tickerHistories = activeTickers.associateWith { ticker ->
        val tickerHoldings = holdings.filter {
            (it.proxySecurity?.tickerSymbol ?: it.security?.tickerSymbol)?.trim()?.equals(ticker.trim(), ignoreCase = true) == true
        }
        val currentInstVal = tickerHoldings.sumOf { it.institutionValue ?: (it.quantity * it.institutionPrice) }

        val rawEntries = securityPerformance?.history?.entries?.firstOrNull {
            it.key.trim().equals(ticker.trim(), ignoreCase = true)
        }?.value

        if (rawEntries.isNullOrEmpty()) {
            listOf(startDate to currentInstVal, today to currentInstVal)
        } else {
            val sortedRaw = rawEntries.mapNotNull { entry ->
                try {
                    LocalDate.parse(entry.date.substringBefore('T')) to entry.value
                } catch (e: Exception) {
                    null
                }
            }.sortedBy { it.first }

            if (sortedRaw.isEmpty()) {
                listOf(startDate to currentInstVal, today to currentInstVal)
            } else {
                val lastRawVal = sortedRaw.last().second
                val scale = if (lastRawVal > 0.0) currentInstVal / lastRawVal else 1.0
                sortedRaw.map { (date, value) -> date to (value * scale) }
            }
        }
    }

    val points = mutableListOf<Pair<LocalDate, Double>>()
    var currentDate = startDate
    while (currentDate <= today) {
        var investmentSum = 0.0
        for ((_, history) in tickerHistories) {
            val match = history.lastOrNull { it.first <= currentDate } ?: history.firstOrNull()
            if (match != null) {
                investmentSum += match.second
            }
        }
        points.add(currentDate to (investmentSum + staticBaseValue))
        currentDate = currentDate.plus(1, DateTimeUnit.DAY)
    }

    return points
}

fun computeDashboardBalanceHistory(
    accounts: List<DashboardAccount>,
    transactions: List<Transaction>,
    holdings: List<Holding> = emptyList(),
    securityPerformance: SecurityPerformance? = null,
    realEstateHoldings: List<RealEstateHolding> = emptyList(),
    timeframe: BalanceTimeframe
): List<Pair<LocalDate, Double>> {
    val today = try {
        Clock.System.todayIn(TimeZone.currentSystemDefault())
    } catch (e: Exception) {
        Clock.System.todayIn(TimeZone.currentSystemDefault())
    }

    val startDate = when (timeframe) {
        BalanceTimeframe.OneMonth -> today.minus(1, DateTimeUnit.MONTH)
        BalanceTimeframe.ThreeMonths -> today.minus(3, DateTimeUnit.MONTH)
        BalanceTimeframe.SixMonths -> today.minus(6, DateTimeUnit.MONTH)
        BalanceTimeframe.OneYear -> today.minus(1, DateTimeUnit.YEAR)
        BalanceTimeframe.YearToDate -> LocalDate(today.year, 1, 1)
    }

    val holdingParsedEstimates = realEstateHoldings.map { holding ->
        val parsed = holding.valueEstimates.mapNotNull { est ->
            try {
                LocalDate.parse(est.date.substringBefore('T')) to est.estimatedValue
            } catch (e: Exception) {
                null
            }
        }.sortedBy { it.first }
        Triple(holding, parsed, holding.mortgageAmount ?: 0.0)
    }

    fun getRealEstateBalance(date: LocalDate): Double {
        return holdingParsedEstimates.sumOf { (holding, parsed, mortgage) ->
            val baseValue = if (parsed.isNotEmpty()) {
                val latestBefore = parsed.filter { it.first <= date }.maxByOrNull { it.first }
                if (latestBefore != null) {
                    latestBefore.second
                } else if (holding.purchasePrice > 0.0) {
                    holding.purchasePrice
                } else {
                    parsed.first().second
                }
            } else {
                holding.purchasePrice
            }
            baseValue - mortgage
        }
    }

    // 1. Calculate non-investment balance history over time by replaying transactions
    val currentNonInvestmentBalance = accounts
        .filter { it.type.trim().lowercase() != "investment" }
        .sumOf {
            val bal = it.currentBalance ?: 0.0
            if (it.type.trim().lowercase() == "credit" || it.type.trim().lowercase() == "loan") -bal else bal
        }

    val nonInvestmentAccountIds = accounts
        .filter { it.type.trim().lowercase() != "investment" }
        .map { it.accountId }
        .toSet()
    val nonInvestmentTx = transactions.filter { it.accountId in nonInvestmentAccountIds }

    val groupedTx = nonInvestmentTx
        .mapNotNull { tx ->
            try {
                LocalDate.parse(tx.date.substringBefore('T')) to tx
            } catch (e: Exception) {
                null
            }
        }
        .groupBy({ pair -> pair.first }, { pair -> pair.second })

    val sortedDates = groupedTx.keys.sortedDescending()
    val dateToNonInvestmentBalance = mutableMapOf<LocalDate, Double>()
    dateToNonInvestmentBalance[today] = currentNonInvestmentBalance

    var runningBalance = currentNonInvestmentBalance
    for (date in sortedDates) {
        dateToNonInvestmentBalance[date] = runningBalance
        val dayTotalAmount = groupedTx[date]?.sumOf { tx -> tx.amount } ?: 0.0
        runningBalance += dayTotalAmount
        dateToNonInvestmentBalance[date.minus(1, DateTimeUnit.DAY)] = runningBalance
    }

    fun getNonInvestmentBalance(date: LocalDate): Double {
        val matchKey = dateToNonInvestmentBalance.keys
            .filter { it >= date }
            .minOrNull()
        return if (matchKey != null) {
            dateToNonInvestmentBalance[matchKey] ?: currentNonInvestmentBalance
        } else {
            runningBalance
        }
    }

    // 2. If there are no investments, return dynamic non-investment balance + real estate list
    val hasInvestment = accounts.any { it.type.trim().lowercase() == "investment" }
    if (!hasInvestment) {
        val points = mutableListOf<Pair<LocalDate, Double>>()
        var currentDate = startDate
        while (currentDate <= today) {
            points.add(currentDate to (getNonInvestmentBalance(currentDate) + getRealEstateBalance(currentDate)))
            currentDate = currentDate.plus(1, DateTimeUnit.DAY)
        }
        return points
    }

    val investmentAccounts = accounts.filter { it.type.trim().lowercase() == "investment" }
    val totalUninvestedCash = investmentAccounts.sumOf { account ->
        val acctHoldings = holdings.filter { it.accountId == account.accountId }
        val acctHoldingsValue = acctHoldings.sumOf { it.institutionValue ?: (it.quantity * it.institutionPrice) }
        val rawAcctBalance = account.currentBalance ?: 0.0
        val effectiveAcctBalance = if (rawAcctBalance >= acctHoldingsValue && rawAcctBalance > 0.0) rawAcctBalance else acctHoldingsValue
        (effectiveAcctBalance - acctHoldingsValue).coerceAtLeast(0.0)
    }
    val nonTickerHoldings = holdings.filter { (it.proxySecurity?.tickerSymbol ?: it.security?.tickerSymbol)?.trim().isNullOrEmpty() }
    val nonTickerHoldingsValue = nonTickerHoldings.sumOf { it.institutionValue ?: (it.quantity * it.institutionPrice) }
    val staticInvestmentBaseValue = totalUninvestedCash + nonTickerHoldingsValue

    val activeTickers = holdings.mapNotNull {
        (it.proxySecurity?.tickerSymbol ?: it.security?.tickerSymbol)?.trim()?.takeIf { sym -> sym.isNotEmpty() }
    }.toSet()

    val tickerHistories = activeTickers.associateWith { ticker ->
        val tickerHoldings = holdings.filter {
            (it.proxySecurity?.tickerSymbol ?: it.security?.tickerSymbol)?.trim()?.equals(ticker.trim(), ignoreCase = true) == true
        }
        val currentInstVal = tickerHoldings.sumOf { it.institutionValue ?: (it.quantity * it.institutionPrice) }

        val rawEntries = securityPerformance?.history?.entries?.firstOrNull {
            it.key.trim().equals(ticker.trim(), ignoreCase = true)
        }?.value

        if (rawEntries.isNullOrEmpty()) {
            listOf(startDate to currentInstVal, today to currentInstVal)
        } else {
            val sortedRaw = rawEntries.mapNotNull { entry ->
                try {
                    LocalDate.parse(entry.date.substringBefore('T')) to entry.value
                } catch (e: Exception) {
                    null
                }
            }.sortedBy { it.first }

            if (sortedRaw.isEmpty()) {
                listOf(startDate to currentInstVal, today to currentInstVal)
            } else {
                val lastRawVal = sortedRaw.last().second
                val scale = if (lastRawVal > 0.0) currentInstVal / lastRawVal else 1.0
                sortedRaw.map { (date, value) -> date to (value * scale) }
            }
        }
    }

    // 4. Generate points for all dates from startDate to today
    val points = mutableListOf<Pair<LocalDate, Double>>()
    var currentDate = startDate
    while (currentDate <= today) {
        var investmentSum = 0.0
        for ((_, history) in tickerHistories) {
            val match = history.lastOrNull { it.first <= currentDate } ?: history.firstOrNull()
            if (match != null) {
                investmentSum += match.second
            }
        }
        points.add(currentDate to (getNonInvestmentBalance(currentDate) + investmentSum + staticInvestmentBaseValue + getRealEstateBalance(currentDate)))
        currentDate = currentDate.plus(1, DateTimeUnit.DAY)
    }

    return points
}
